// SPDX-License-Identifier: GPL-3.0-or-later

/** Verified range storage ownership, recoverable book inventories, and cross-tab retirement. */

import type { MultikeyIndex, MultikeyRange } from "./multikey-index";
import {
  MULTIKEY_LEGACY_SOURCE_PREFIX,
  MULTIKEY_RANGE_CACHE,
  MULTIKEY_SOURCE_META_CACHE,
  multikeyRangeMarkerUrl,
} from "./multikey-cache-policy";

/** Browser storage port; reduced hosts may omit persistence/enumeration capabilities. */
export type MultikeySourceStorage = Pick<CacheStorage, "match"> &
  Partial<Pick<CacheStorage, "open" | "keys" | "delete">>;

/** An edition lease is not refreshed by prefetch or foreground reads from an old tab. */
export interface MultikeySourceInventory {
  readonly owner: string;
  readonly token: string;
}

/** A retired reader must reload rather than restore an earlier cache edition. */
export class MultikeySourceEditionError extends Error {}

interface Entry {
  url: string;
  identity: string;
  byteLength: number;
  firstPage: number;
  lastPage: number;
  key: string;
}
interface Inventory {
  version: 2;
  token: string;
  canonical: string;
  canonicalUrl?: string;
  target: Entry[];
  retained: Entry[];
}

const BOOK_PATH = "/__sb-multikey-cache/book";
const STAGING_PATH = "/__sb-multikey-cache/staged";
const IDENTITY_HEADER = "X-SB-Multikey-Sha256";
const tails = new WeakMap<object, Promise<unknown>>();

/** Serializes storage mutations across tabs; reduced test/host environments serialize locally. */
async function locked<T>(storage: object, signal: AbortSignal, work: () => Promise<T>): Promise<T> {
  signal.throwIfAborted();
  if (globalThis.navigator?.locks)
    return navigator.locks.request("sb-multikey-source-cache-v2", { signal }, work);
  if (typeof window !== "undefined")
    throw new Error("Safe multikey source persistence requires Web Locks");
  const previous = tails.get(storage) ?? Promise.resolve();
  const next = previous
    .catch(() => {})
    .then(() => {
      signal.throwIfAborted();
      return work();
    });
  tails.set(storage, next);
  try {
    return await next;
  } finally {
    if (tails.get(storage) === next) tails.delete(storage);
  }
}

/** Separates metadata keys from real document/index URLs. */
function bookKey(owner: string): string {
  const key = new URL(BOOK_PATH, owner);
  key.searchParams.set("url", owner);
  return key.href;
}

/** Staging is needed only for a reused URL whose old and new page boundaries differ. */
function stagedKey(owner: string, token: string, url: string): string {
  const key = new URL(STAGING_PATH, owner);
  key.searchParams.set("book", owner);
  key.searchParams.set("edition", token);
  key.searchParams.set("url", url);
  return key.href;
}

/** Stores only source identity/bounds, not musical metadata or generated artifacts. */
function entries(index: MultikeyIndex): Entry[] {
  return index.ranges.map(({ url, identity, byteLength, firstPage, lastPage }) => ({
    url,
    identity,
    byteLength,
    firstPage,
    lastPage,
    key: url,
  }));
}

/** Rejects damaged inventory metadata rather than guessing which payloads may be deleted. */
async function readInventory(cache: Cache, owner: string): Promise<Inventory | undefined> {
  const response = await cache.match(bookKey(owner));
  if (!response) return undefined;
  const value: Inventory = await response.json();
  const valid = (entry: Entry) =>
    entry &&
    typeof entry.url === "string" &&
    /^https?:\/\//.test(entry.url) &&
    typeof entry.key === "string" &&
    /^https?:\/\//.test(entry.key) &&
    /^[a-f0-9]{64}$/.test(entry.identity) &&
    Number.isSafeInteger(entry.byteLength) &&
    entry.byteLength > 0 &&
    Number.isSafeInteger(entry.firstPage) &&
    entry.firstPage > 0 &&
    Number.isSafeInteger(entry.lastPage) &&
    entry.lastPage >= entry.firstPage;
  if (
    value?.version !== 2 ||
    typeof value.token !== "string" ||
    typeof value.canonical !== "string" ||
    !Array.isArray(value.target) ||
    !Array.isArray(value.retained) ||
    !value.target.every(valid) ||
    !value.retained.every(valid)
  )
    throw new Error("Invalid multikey source inventory");
  return value;
}

/** Metadata publication precedes destructive cleanup so interruption leaves recoverable references. */
async function saveInventory(cache: Cache, owner: string, value: Inventory): Promise<void> {
  await cache.put(
    bookKey(owner),
    new Response(JSON.stringify(value), {
      headers: { "Content-Type": "application/json" },
    }),
  );
}

/** Prevents stale readers from publishing or deleting a newer edition's cache. */
async function currentInventory(cache: Cache, lease: MultikeySourceInventory): Promise<Inventory> {
  const value = await readInventory(cache, lease.owner);
  if (!value) throw new Error("Multikey source inventory was evicted or storage is unavailable");
  if (value.token !== lease.token)
    throw new MultikeySourceEditionError(
      "Songbook sources changed in another tab; reload this songbook",
    );
  return value;
}

/** Marks URLs before any download, including URLs retired from the current edition. */
async function markUrls(meta: Cache, values: Entry[]): Promise<void> {
  for (const { url } of values) await meta.put(multikeyRangeMarkerUrl(url), new Response(null));
}

/** Another book may still need different bytes at this URL; keep its actual key intact. */
async function foreignReference(meta: Cache, owner: string, entry: Entry): Promise<boolean> {
  for (const request of await meta.keys()) {
    const key = new URL(request.url);
    if (key.pathname !== BOOK_PATH) continue;
    const otherOwner = key.searchParams.get("url")!;
    if (otherOwner === owner) continue;
    const value = await readInventory(meta, otherOwner);
    if (
      [...(value?.target ?? []), ...(value?.retained ?? [])].some(
        other => other.key === entry.url && other.identity !== entry.identity,
      )
    )
      return true;
  }
  return false;
}

/** Claims a freshly loaded index, retaining interrupted and previous inventories until replacement. */
export async function beginMultikeySourceInventory(
  index: MultikeyIndex,
  previous: MultikeyIndex | undefined,
  storage: MultikeySourceStorage | undefined,
  signal: AbortSignal,
  canonicalUrl?: string,
): Promise<MultikeySourceInventory | undefined> {
  if (!storage?.open) return undefined;
  try {
    return await locked(storage, signal, async () => {
      const meta = await storage.open!(MULTIKEY_SOURCE_META_CACHE);
      const old = await readInventory(meta, index.url);
      const target = entries(index);
      if (
        old &&
        old.canonical === index.canonical.identity &&
        JSON.stringify(old.target.map(({ key: _key, ...entry }) => entry)) ===
          JSON.stringify(target.map(({ key: _key, ...entry }) => entry))
      ) {
        if (canonicalUrl && old.canonicalUrl !== canonicalUrl) {
          old.canonicalUrl = canonicalUrl;
          await saveInventory(meta, index.url, old);
        }
        await markUrls(meta, old.target);
        return { owner: index.url, token: old.token };
      }
      // This is a uniqueness token, not a secret; hashing deliberately works without WebCrypto.
      const token =
        globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const retained = old ? [...old.target, ...old.retained] : previous ? entries(previous) : [];
      for (const entry of target)
        if (
          retained.some(
            old =>
              old.url === entry.url &&
              (old.firstPage !== entry.firstPage || old.lastPage !== entry.lastPage),
          ) ||
          (await foreignReference(meta, index.url, entry))
        )
          entry.key = stagedKey(index.url, token, entry.url);
      const unique = [...new Map(retained.map(entry => [JSON.stringify(entry), entry])).values()];
      await markUrls(meta, [...target, ...unique]);
      signal.throwIfAborted();
      await saveInventory(meta, index.url, {
        version: 2,
        token,
        canonical: index.canonical.identity,
        canonicalUrl: canonicalUrl ?? old?.canonicalUrl,
        target,
        retained: unique,
      });
      return { owner: index.url, token };
    });
  } catch {
    signal.throwIfAborted();
    // Ordinary reading and verified online processing do not require persistent storage.
    return undefined;
  }
}

/** Explicit consumer-configured URLs of books eligible for background source maintenance. */
export interface MultikeySourceBook {
  readonly indexUrl: string;
  readonly canonicalUrl: string;
}

/** Lists configured books without deriving canonical URLs from published filenames. */
export async function listMultikeySourceBooks(
  storage: MultikeySourceStorage | undefined,
  signal: AbortSignal,
): Promise<MultikeySourceBook[]> {
  if (!storage?.open) return [];
  return locked(storage, signal, async () => {
    const meta = await storage.open!(MULTIKEY_SOURCE_META_CACHE);
    const books: MultikeySourceBook[] = [];
    for (const request of await meta.keys()) {
      signal.throwIfAborted();
      const key = new URL(request.url);
      if (key.pathname !== BOOK_PATH) continue;
      const owner = key.searchParams.get("url");
      if (!owner || !/^https?:\/\//.test(owner)) continue;
      try {
        const value = await readInventory(meta, owner);
        if (typeof value?.canonicalUrl === "string" && /^https?:\/\//.test(value.canonicalUrl))
          books.push({ indexUrl: owner, canonicalUrl: value.canonicalUrl });
      } catch {
        signal.throwIfAborted();
        // A damaged book record must not prevent maintenance of other books.
      }
    }
    return books;
  });
}

/** Resolves the active edition's physical key, never treating a URL match as identity proof. */
async function rangeEntry(
  meta: Cache,
  range: MultikeyRange,
  lease?: MultikeySourceInventory,
): Promise<Entry> {
  if (!lease) return { ...range, key: range.url };
  const inventory = await currentInventory(meta, lease);
  const entry = inventory.target.find(
    entry => entry.url === range.url && entry.identity === range.identity,
  );
  if (!entry) throw new Error("Range is not part of the active songbook inventory");
  return entry;
}

/** Reads only dedicated storage; the caller must verify the actual response bytes. */
export async function readMultikeySource(
  range: MultikeyRange,
  storage: MultikeySourceStorage | undefined,
  signal: AbortSignal,
  lease?: MultikeySourceInventory,
): Promise<Response | undefined> {
  if (!storage?.open) return undefined;
  return locked(storage, signal, async () => {
    const meta = await storage.open!(MULTIKEY_SOURCE_META_CACHE);
    const entry = await rangeEntry(meta, range, lease);
    return (await storage.open!(MULTIKEY_RANGE_CACHE)).match(entry.key, { ignoreVary: true });
  });
}

/** All inventories participate in deletion guards, including books sharing a source URL. */
async function referencedKeys(meta: Cache): Promise<Set<string>> {
  const keys = new Set<string>();
  for (const request of await meta.keys()) {
    const url = new URL(request.url);
    if (url.pathname !== BOOK_PATH) continue;
    const value = await readInventory(meta, url.searchParams.get("url")!);
    for (const entry of [...(value?.target ?? []), ...(value?.retained ?? [])]) keys.add(entry.key);
  }
  return keys;
}

/** Removes old payloads only after metadata and all other book references have been checked. */
async function discardUnused(cache: Cache, meta: Cache): Promise<void> {
  const keep = await referencedKeys(meta);
  // Also recover garbage left by a closed tab between metadata publication and deletion.
  for (const request of await cache.keys()) if (!keep.has(request.url)) await cache.delete(request);
}

/** Removes duplicate legacy/runtime payloads for a URL only after its replacement is persisted. */
async function retireLegacyUrl(storage: MultikeySourceStorage, url: string): Promise<void> {
  const meta = await storage.open!(MULTIKEY_SOURCE_META_CACHE);
  const payloads = await storage.open!(MULTIKEY_RANGE_CACHE);
  const protectedHashes = new Set<string>();
  for (const request of await meta.keys()) {
    const key = new URL(request.url);
    if (key.pathname !== BOOK_PATH) continue;
    const inventory = await readInventory(meta, key.searchParams.get("url")!);
    for (const entry of [...(inventory?.target ?? []), ...(inventory?.retained ?? [])]) {
      if (entry.url !== url) continue;
      const current = await payloads.match(entry.key);
      const identity = current?.headers.get(IDENTITY_HEADER);
      void current?.body?.cancel().catch(() => {});
      if (identity !== entry.identity) protectedHashes.add(entry.identity);
    }
  }
  for (const name of (await storage.keys?.()) ?? []) {
    if (name !== "sb-runtime-v2" && !name.startsWith(MULTIKEY_LEGACY_SOURCE_PREFIX)) continue;
    if (
      name === "sb-runtime-v2"
        ? protectedHashes.size > 0
        : protectedHashes.has(name.slice(MULTIKEY_LEGACY_SOURCE_PREFIX.length))
    )
      continue;
    const cache = await storage.open!(name);
    await cache.delete(url);
    if (name.startsWith(MULTIKEY_LEGACY_SOURCE_PREFIX) && !(await cache.keys()).length)
      await storage.delete?.(name);
  }
}

/** Sweeps orphaned hash caches using all cached indices plus interrupted/current inventory references. */
export async function sweepLegacyMultikeySources(
  storage: MultikeySourceStorage | undefined,
  indexes: readonly MultikeyIndex[],
  signal: AbortSignal,
): Promise<void> {
  if (!storage?.open || !storage.keys) return;
  await locked(storage, signal, async () => {
    const meta = await storage.open!(MULTIKEY_SOURCE_META_CACHE);
    const keep = new Set(
      indexes.flatMap(index => index.ranges.map(range => `${range.identity}:${range.url}`)),
    );
    for (const request of await meta.keys()) {
      const key = new URL(request.url);
      if (key.pathname !== BOOK_PATH) continue;
      const inventory = await readInventory(meta, key.searchParams.get("url")!);
      for (const entry of [...(inventory?.target ?? []), ...(inventory?.retained ?? [])])
        keep.add(`${entry.identity}:${entry.url}`);
    }
    for (const name of await storage.keys!()) {
      if (!name.startsWith(MULTIKEY_LEGACY_SOURCE_PREFIX)) continue;
      const identity = name.slice(MULTIKEY_LEGACY_SOURCE_PREFIX.length);
      if (!/^[a-f0-9]{64}$/.test(identity)) continue;
      const cache = await storage.open!(name);
      for (const request of await cache.keys()) {
        signal.throwIfAborted();
        if (keep.has(`${identity}:${request.url}`)) continue;
        await meta.put(multikeyRangeMarkerUrl(request.url), new Response(null));
        await cache.delete(request);
        await retireLegacyUrl(storage, request.url);
      }
      if (!(await cache.keys()).length) await storage.delete?.(name);
    }
  });
}

/** Writes verified bytes, re-verifies persistence, and incrementally retires equal-bound replacements. */
export async function persistMultikeySource(
  range: MultikeyRange,
  blob: Blob,
  headers: Headers,
  storage: MultikeySourceStorage | undefined,
  signal: AbortSignal,
  verify: (response: Response) => Promise<void>,
  lease?: MultikeySourceInventory,
): Promise<void> {
  if (!storage?.open) return;
  await locked(storage, signal, async () => {
    const meta = await storage.open!(MULTIKEY_SOURCE_META_CACHE);
    const entry = await rangeEntry(meta, range, lease);
    await markUrls(meta, [entry]);
    const cache = await storage.open!(MULTIKEY_RANGE_CACHE);
    const clean = new Headers(headers);
    for (const name of ["content-encoding", "content-range", "vary"]) clean.delete(name);
    clean.set("content-length", String(blob.size));
    clean.set(IDENTITY_HEADER, range.identity);
    signal.throwIfAborted();
    await cache.put(entry.key, new Response(blob, { headers: clean }));
    const stored = await cache.match(entry.key);
    if (!stored) throw new Error("Multikey source persistence failed");
    await verify(stored);
    signal.throwIfAborted();
    if (lease) {
      const inventory = await currentInventory(meta, lease);
      const retired = inventory.retained.filter(
        old =>
          old.firstPage === entry.firstPage &&
          old.lastPage === entry.lastPage &&
          (old.key !== entry.key || old.identity !== entry.identity),
      );
      inventory.retained = inventory.retained.filter(old => !retired.includes(old));
      await saveInventory(meta, lease.owner, inventory);
      await discardUnused(cache, meta);
      for (const old of retired) await retireLegacyUrl(storage, old.url);
    }
    await retireLegacyUrl(storage, range.url);
  });
}

/** Verifies a complete edition before promoting staged collisions and sweeping regrouped/removed ranges. */
export async function completeMultikeySourceInventory(
  storage: MultikeySourceStorage | undefined,
  signal: AbortSignal,
  verify: (
    response: Response,
    range: Pick<MultikeyRange, "identity" | "byteLength">,
  ) => Promise<void>,
  index: MultikeyIndex,
  lease?: MultikeySourceInventory,
): Promise<void> {
  if (!storage?.open || !lease) return;
  await locked(storage, signal, async () => {
    const meta = await storage.open!(MULTIKEY_SOURCE_META_CACHE);
    const inventory = await currentInventory(meta, lease);
    const cache = await storage.open!(MULTIKEY_RANGE_CACHE);
    for (const range of index.ranges) {
      const entry = inventory.target.find(entry => entry.url === range.url)!;
      const response = await cache.match(entry.key);
      if (!response) throw new Error("Multikey source cache is incomplete");
      await verify(response, range);
    }
    const retired = [
      ...inventory.retained,
      ...inventory.target.filter(entry => entry.key !== entry.url),
    ];
    for (const entry of inventory.target) {
      signal.throwIfAborted();
      if (entry.key === entry.url) continue;
      if (await foreignReference(meta, lease.owner, entry)) continue;
      await cache.put(entry.url, (await cache.match(entry.key))!);
      await verify(
        (await cache.match(entry.url))!,
        index.ranges.find(range => range.url === entry.url)!,
      );
    }
    for (const entry of inventory.target)
      if (!(await foreignReference(meta, lease.owner, entry))) entry.key = entry.url;
    inventory.retained = [];
    signal.throwIfAborted();
    await saveInventory(meta, lease.owner, inventory);
    // Release a shared-URL staging exception once the other book has moved on too.
    for (const request of await meta.keys()) {
      const key = new URL(request.url);
      if (key.pathname !== BOOK_PATH) continue;
      const owner = key.searchParams.get("url")!;
      const value = await readInventory(meta, owner);
      if (!value) continue;
      let changed = false;
      for (const entry of value.target) {
        if (entry.key === entry.url || (await foreignReference(meta, owner, entry))) continue;
        const response = await cache.match(entry.url);
        if (!response) continue;
        if (response.headers.get(IDENTITY_HEADER) !== entry.identity) {
          void response.body?.cancel().catch(() => {});
          continue;
        }
        await verify(response, entry);
        entry.key = entry.url;
        changed = true;
      }
      if (changed) await saveInventory(meta, owner, value);
    }
    await discardUnused(cache, meta);
    for (const entry of [...inventory.target, ...retired])
      await retireLegacyUrl(storage, entry.url);
  });
}
